import React, { useEffect, useState, useCallback } from "react";
import {
  Row,
  Col,
  Pagination,
  Collapse,
  Button,
  InputNumber,
  Space,
  Switch,
  Table,
  Tooltip,
  Typography,
  Form,
  Input,
  message,
  Select,
  Upload,
  Modal,
} from "antd";
import axios from "axios";
import PageHeader from "../Layout/PageHeader";
import {
  CaretRightOutlined,
  PlusOutlined,
  SaveOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { previewBase64Image } from "../../helpers/image";
import { formatDateTime } from "../../helpers/date";
import BannerFilters from "./BannerFilters";
import { PERMISSIONS } from "../../constants/roles";
import { usePermissions } from "../App/PermissionContext";

const Banner = () => {
  const { permissions } = usePermissions();

  const [pagination, setPagination] = useState({ currentPage: 1, limit: 10 });
  const [banners, setBanners] = useState([]);
  const [isActive, setIsActive] = useState(false);
  const [uploadedFile, setUploadedFile] = useState();
  const [loading, setLoading] = useState({
    index: false,
    create: false,
    update: false,
  });
  const [filters, setFilters] = useState({
    isActive: null,
  });
  const [form] = Form.useForm();

  const fetchBanner = useCallback(async () => {
    setLoading((prevLoading) => ({ ...prevLoading, index: true }));

    const params = {
      ...(filters.isActive !== null && { isActive: filters.isActive }),
      limit: pagination.limit,
      page: pagination.currentPage,
    };

    const res = await axios.get("/api/banners", { params });

    setBanners(res.data.data);
    setPagination(res.data.pagination);
    setLoading((prevLoading) => ({ ...prevLoading, index: false }));
  }, [pagination.currentPage, pagination.limit, filters]);

  const createHandler = async (newBanner) => {
    setLoading((prevLoading) => ({ ...prevLoading, create: true }));

    try {
      const formData = new FormData();

      Object.entries(newBanner).forEach(([key, value]) => {
        formData.append(key, value);
      });

      if (uploadedFile) {
        formData.append("image", uploadedFile);
      }

      await axios.post("/api/banners", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      form.resetFields();
      message.success("Banner created");
      fetchBanner();
    } catch (error) {
      message.error(error.response?.data.message || error.message);
    }

    setLoading((prevLoading) => ({ ...prevLoading, create: false }));
  };

  const updateIsActiveStatus = async (row, value) => {
    setLoading((prevLoading) => ({ ...prevLoading, update: true }));

    await axios
      .patch(`/api/banner/${row.objectId}`, {
        isActive: value,
      })
      .then((res) => {
        message.info(`Banner ${value ? "Enabled" : "Disabled"}`);
        fetchBanner();
      })
      .catch((error) => {
        message.error(error.message);
      });

    setLoading((prevLoading) => ({ ...prevLoading, update: false }));
  };

  const updateBanner = async (row, params) => {
    setLoading((prevLoading) => ({ ...prevLoading, update: true }));

    await axios
      .patch(`/api/banner/${row.objectId}`, params)
      .then((res) => {
        message.info(`Banner sequence updated`);
        fetchBanner();
      })
      .catch((error) => {
        message.error(error.message);
      });

    setLoading((prevLoading) => ({ ...prevLoading, update: false }));
  };

  const archiveHandler = async (bannerId) => {
    try {
      const messageContent = (
        <p>
          Are you sure you want to delete this banner?
          <br />
        </p>
      );

      Modal.confirm({
        content: messageContent,
        title: `Delete Banner? `,
        async onOk() {
          await axios
            .delete(`/api/banner/${bannerId}`)
            .then((res) => {
              message.success(`Banner deleted.`);
              fetchBanner();
            })
            .catch((error) => {
              message.error(error.message);
            });
        },
      });
    } catch (error) {
      message.error(error.response.data.message || error.message);
    }
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      width: 100,
    },
    {
      title: "link",
      dataIndex: "link",
      width: 100,
      render: (text, row) => {
        return (
          <a href={row.link} target="_blank">
            <Typography.Link>{row.link}</Typography.Link>
          </a>
        );
      },
    },
    {
      title: "CTA (English)",
      dataIndex: "cta_en",
      width: 166,
      render: (text, row) => {
        return permissions.includes(PERMISSIONS.UPDATE_BANNER) ? (
          <Input
            value={text}
            onChange={(e) => {
              setBanners(
                banners.map((banner) =>
                  banner.objectId === row.objectId
                    ? { ...banner, cta_en: e.target.value }
                    : banner
                )
              );
            }}
          />
        ) : text
      }
    },
    {
      title: "CTA (Malay)",
      dataIndex: "cta_ms",
      width: 166,
      render: (text, row) => {
        return permissions.includes(PERMISSIONS.UPDATE_BANNER) ? (
          <Input
            value={text}
            onChange={(e) => {
              setBanners(
                banners.map((banner) =>
                  banner.objectId === row.objectId
                    ? { ...banner, cta_en: e.target.value }
                    : banner
                )
              );
            }}
          />
        ) : text
      },
    },
    {
      title: "CTA (Chinese)",
      dataIndex: "cta_zh",
      width: 166,
      render: (text, row) => {
        return permissions.includes(PERMISSIONS.UPDATE_BANNER) ? (
          <Input
            value={text}
            onChange={(e) => {
              setBanners(
                banners.map((banner) =>
                  banner.objectId === row.objectId
                    ? { ...banner, cta_en: e.target.value }
                    : banner
                )
              );
            }}
          />
        ) : text
      },
    },
    {
      title: "Sequence",
      dataIndex: "sequence",
      width: 100,
      render: (text, row) => {
        return permissions.includes(PERMISSIONS.UPDATE_BANNER) ? (
          <InputNumber
            value={text}
            precision={0}
            onChange={(value) => {
              setBanners(
                banners.map((banner) =>
                  banner.objectId === row.objectId
                    ? { ...banner, sequence: value }
                    : banner
                )
              );
            }}
          />
        ) : text
      }
    },
    {
      title: "Is Active",
      dataIndex: "isActive",
      width: 130,
      render: (text, row) => {
        return permissions.includes(PERMISSIONS.UPDATE_BANNER) ? (
          <Space>
            <Switch
              checked={text}
              onChange={(checked) => {
                updateIsActiveStatus(row, checked);
              }}
            />
          </Space>
        ) : text ? text : "-";
      }
    },
    {
      title: "Image",
      dataIndex: "Image",
      width: 70,
      render: (text, row) => {
        return (
          <a href={row.image} target="_blank">
            <Typography.Link>View</Typography.Link>
          </a>
        );
      },
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      width: 160,
      render: (createdAt) => formatDateTime(createdAt),
    },
    ...(permissions.includes(PERMISSIONS.UPDATE_BANNER) ? [{
      title: "",
      dataIndex: "action",
      fixed: "right",
      width: 100,
      render: (_, row) => (
        <Space>
          <Tooltip title="Update">
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={() =>
                updateBanner(row, {
                  sequence: row.sequence,
                  cta_en: row.cta_en,
                  cta_ms: row.cta_ms,
                  cta_zh: row.cta_zh,
                })
              }
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button
              type="primary"
              danger
              icon={<DeleteOutlined />}
              onClick={() => archiveHandler(row.objectId)}
            />
          </Tooltip>
        </Space>
      ),
    }] : [])

  ];

  useEffect(() => {
    fetchBanner();
  }, [pagination.currentPage, pagination.limit, fetchBanner]);

  return (
    <div>
      <PageHeader
        title="Banner"
        childRoutes={[{ path: "/banner", breadcrumbName: "Banner" }]}
      />

      {permissions.includes(PERMISSIONS.CREATE_BANNER) && (<Collapse
        expandIcon={({ isActive }) => (
          <CaretRightOutlined rotate={isActive ? 90 : 0} />
        )}
      >
        <Collapse.Panel header="Create New Banner" key="create-banner-section">
          <Form
            form={form}
            name="new-banner-form"
            labelCol={{ span: 8 }}
            wrapperCol={{ span: 16 }}
            autoComplete="off"
            onFinish={createHandler}
          >
            <Form.Item
              label="Name/Label (Must be unique)"
              name="name"
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>

            <Form.Item label="Attachments" name="attachments">
              <Upload
                beforeUpload={(file) => {
                  console.log(file);
                  const isLt5M = file.size / 1024 / 1024 < 5;
                  if (!isLt5M) {
                    message.error("File must be smaller than 5MB!");
                    return Upload.LIST_IGNORE;
                  }
                  return false;
                }}
                listType="picture-card"
                onChange={(e) => {
                  if (e.file.status === "removed") {
                    setUploadedFile(null);
                    return;
                  }
                  setUploadedFile(e.file);
                }}
                onPreview={(file) => previewBase64Image(file)}
                maxCount={1}
              >
                <div>
                  <PlusOutlined />
                  <div style={{ marginTop: 8 }}>Upload</div>
                </div>
              </Upload>
            </Form.Item>

            <Form.Item label="Link" name="link" rules={[{ required: true }]}>
              <Input />
            </Form.Item>

            <Form.Item
              label="Active"
              name="isActive"
              rules={[{ required: true }]}
            >
              <Select value={isActive} onChange={setIsActive}>
                <Select.Option value={true}>Yes</Select.Option>
                <Select.Option value={false}>No</Select.Option>
              </Select>
            </Form.Item>

            <Form.Item
              label="CTA (English)"
              name="cta_en"
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              label="CTA (Malay)"
              name="cta_ms"
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              label="CTA (Chinese)"
              name="cta_zh"
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              label="Sequence"
              name="sequence"
              rules={[{ required: true }]}
            >
              <InputNumber step={1} />
            </Form.Item>

            <Form.Item wrapperCol={{ offset: 8, span: 16 }}>
              <Button loading={loading.create} type="primary" htmlType="submit">
                Create
              </Button>
            </Form.Item>
          </Form>
        </Collapse.Panel>

        <Collapse
          expandIcon={({ isActive }) => (
            <CaretRightOutlined rotate={isActive ? 90 : 0} />
          )}
        >
          <Collapse.Panel header="Filters" key="filters">
            <BannerFilters
              filters={filters}
              onFiltersChange={(filters) => setFilters(filters)}
            />
          </Collapse.Panel>
        </Collapse>
      </Collapse>)}



      <Row justify="end">
        <Col>
          <Pagination
            total={pagination?.totalSize}
            pageSize={pagination?.limit}
            onChange={(currentPage, limit) => {
              setPagination({ ...pagination, limit, currentPage });
            }}
            current={pagination.currentPage}
          />
        </Col>
      </Row>

      <Table
        rowKey="bannerId"
        pagination={false}
        columns={columns}
        dataSource={banners}
        loading={loading.index || loading.update || loading.create}
        scroll={{ x: 1000, y: 500 }}
      />

      <Row justify="end">
        <Col>
          <Pagination
            total={pagination?.totalSize}
            pageSize={pagination?.limit}
            onChange={(currentPage, limit) => {
              setPagination({ ...pagination, limit, currentPage });
            }}
            current={pagination.currentPage}
          />
        </Col>
      </Row>
    </div>
  );
};

export default Banner;
